import { passesMa250Filter } from '@/views/GapUpAnalysis/assets/js/ma250Filter'
import { passesChipBands } from '@/utils/hfbChipFilters'

/** 中/短期波动多选区间：存小数口径（旧固定档，兼容） */
export const CHIP_BAND_OPTIONS = [
  { value: 'lt5', label: '<5%', min: null, max: 0.05 },
  { value: '5_10', label: '5%-10%', min: 0.05, max: 0.1 },
  { value: '10_15', label: '10%-15%', min: 0.1, max: 0.15 }
]

export function isMainBoard(codeOrTsCode) {
  const code = String(codeOrTsCode || '').split('.')[0]
  return code.startsWith('60') || code.startsWith('00')
}

/** 创业板：300 / 301 */
export function isChiNextBoard(codeOrTsCode) {
  const code = String(codeOrTsCode || '').split('.')[0]
  return code.startsWith('300') || code.startsWith('301')
}

export function passesMarketCapFilter(stock, maxYi = 70) {
  const cap = Number(stock?.freeFloatMarketCap ?? stock?.marketValue)
  if (!Number.isFinite(cap)) return false
  return cap <= maxYi
}

/** 首板日收盘价上限（元）；无有效价时剔除 */
export function passesFirstBoardPriceFilter(stock, maxYuan = 20) {
  const p = Number(stock?.firstBoardPrice ?? stock?.closePrice)
  if (!Number.isFinite(p)) return false
  const lim = Number(maxYuan)
  const max = Number.isFinite(lim) ? lim : 20
  return p <= max
}

/** @deprecated 旧单上限；保留兼容 */
export function passesRelativeVolatilityFilter(stock, maxVol) {
  if (maxVol == null || maxVol === '') return true
  const lim = Number(maxVol)
  if (!Number.isFinite(lim)) return true
  const rv = Number(stock?.relativeVolatility ?? stock?.maChip)
  if (!Number.isFinite(rv)) return true
  return rv <= lim
}

/**
 * 筹码波动区间多选（OR）。
 * - 传 options 时按可配档（历史首板口径）
 * - 否则回退 CHIP_BAND_OPTIONS
 * 字段无值时不剔除。
 */
export function passesChipBandFilter(value, selectedBands, options) {
  if (options) return passesChipBands(value, selectedBands, options)
  const bands = Array.isArray(selectedBands) ? selectedBands.filter(Boolean) : []
  if (!bands.length) return true
  const v = Number(value)
  if (!Number.isFinite(v)) return true
  for (const key of bands) {
    const opt = CHIP_BAND_OPTIONS.find((o) => o.value === key)
    if (!opt) continue
    const geMin = opt.min == null ? true : v >= opt.min
    const ltMax = opt.max == null ? true : v < opt.max
    if (geMin && ltMax) return true
  }
  return false
}

export function filterLadderStocks(
  stocks,
  {
    onlyMainBoard,
    onlyChiNext,
    filterMarketValue,
    marketCapMaxYi,
    filterMa250,
    filterRelativeVolMax,
    filterMidChipBands,
    filterShortChipBands,
    filterLongChipBands,
    shortChipOptions,
    midChipOptions,
    longChipOptions,
    filterDistToYearHigh,
    distToYearHighMaxValue,
    filterPreScreenScore,
    preScreenScoreMinValue
  }
) {
  return (stocks || []).filter((stock) => {
    const code = stock.ts_code || stock.code
    if (onlyMainBoard && !isMainBoard(code)) return false
    if (onlyChiNext && !isChiNextBoard(code)) return false
    if (filterMarketValue && !passesMarketCapFilter(stock, marketCapMaxYi ?? 70)) return false
    if (filterMa250) {
      if (
        !passesMa250Filter({
          firstBoardPrice: stock.firstBoardPrice ?? stock.closePrice,
          maMax: stock.maMax,
          ma10: stock.ma10,
          ma20: stock.ma20,
          ma30: stock.ma30,
          ma40: stock.ma40,
          ma60: stock.ma60,
          ma110: stock.ma110,
          ma120: stock.ma120,
          ma160: stock.ma160,
          ma210: stock.ma210,
          ma250: stock.ma250
        })
      ) {
        return false
      }
    }
    if (filterDistToYearHigh) {
      const thr = Number(distToYearHighMaxValue)
      const maxPct = Number.isFinite(thr) ? thr : 15
      const v = Number(stock.distToYearHighPct)
      if (!Number.isFinite(v) || v > maxPct) return false
    }
    if (filterPreScreenScore) {
      const thr = Number(preScreenScoreMinValue)
      const minScore = Number.isFinite(thr) ? thr : 45
      const v = Number(stock.preScreenScore)
      if (!Number.isFinite(v) || v < minScore) return false
    }
    // 新旧筛选：有 bands 用新逻辑；否则回退旧 max
    if (
      filterMidChipBands != null ||
      filterShortChipBands != null ||
      filterLongChipBands != null
    ) {
      if (!passesChipBandFilter(stock.shortChip, filterShortChipBands, shortChipOptions)) return false
      if (!passesChipBandFilter(stock.midChip, filterMidChipBands, midChipOptions)) return false
      if (!passesChipBandFilter(stock.longChip, filterLongChipBands, longChipOptions)) return false
    } else if (!passesRelativeVolatilityFilter(stock, filterRelativeVolMax)) {
      return false
    }
    return true
  })
}

export { passesMa250Filter }

export const BOARD_TIER_BUTTON_DEFS = [
  { key: 'b1', label: '首板' },
  { key: 'b2', label: '二板' },
  { key: 'b3plus', label: '三板及以上' }
]

export function getBoardTier(stock) {
  const boards = Number(stock?.consecutiveBoards) || 1
  if (boards <= 1) return 'b1'
  if (boards === 2) return 'b2'
  return 'b3plus'
}

/** 梯队多选 OR：未选则不过滤 */
export function passesBoardTierFilter(stock, selectedTiers) {
  const tiers = Array.isArray(selectedTiers) ? selectedTiers.filter(Boolean) : []
  if (!tiers.length) return true
  return tiers.includes(getBoardTier(stock))
}
